import type { EditorDocument, SerializedNodeLike } from "./types";

export const HASHTAG_NODE_TYPE = "hashtag";

const HASH = /^[#＃]/;

// Letters, digits, marks (the accents of a decomposed letter), and `_`, from any script. A tag has
// at least one letter, so `#1` is a number. It never starts inside a word, or after `/` or `&`,
// where it would be a URL fragment or an HTML entity.
const WORD = String.raw`\p{L}\p{N}\p{M}_`;
const TAG = new RegExp(String.raw`(^|[^${WORD}&/])([#＃])([${WORD}]*\p{L}[${WORD}]*)`, "u");

/**
 * Where the first hashtag in `text` is, as offsets `[start, end)`, or `null`. The one rule for what
 * a tag is: the editor makes tags with it, and validation holds stored ones to it.
 */
export function findHashtag(text: string): { start: number; end: number } | null {
  const match = TAG.exec(text);
  if (match === null) return null;
  const start = match.index + match[1]!.length;
  return { start, end: start + match[2]!.length + match[3]!.length };
}

/** Whether `text` is exactly one hashtag, as a stored hashtag node's text must be. */
export function isHashtagText(text: string): boolean {
  const match = findHashtag(text);
  return match !== null && match.start === 0 && match.end === text.length;
}

/** A hashtag's name, without its hash: `#Launch` is `Launch`. */
export const tagName = (text: string): string => text.replace(HASH, "");

/**
 * The tags a document holds, by name, in the order they first appear. Tags that differ only in case
 * are one tag, named as it was first written.
 */
export function collectTags(document: EditorDocument): string[] {
  const seen = new Set<string>();
  const tags: string[] = [];
  const stack: SerializedNodeLike[] = [document.content.root as SerializedNodeLike];
  while (stack.length > 0) {
    const node = stack.pop()!;
    if (node.type === HASHTAG_NODE_TYPE && typeof node.text === "string") {
      const name = tagName(node.text);
      const key = name.toLowerCase();
      if (name !== "" && !seen.has(key)) {
        seen.add(key);
        tags.push(name);
      }
    }
    const children = node.children;
    if (Array.isArray(children)) {
      for (let index = children.length - 1; index >= 0; index--) stack.push(children[index]!);
    }
  }
  return tags;
}
